import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Box, Button, Typography } from '@mui/material';
import CustomPagination from 'components/common/pagination/pagination';
import SiteSelector from 'components/common/SiteSelector/SiteSelector';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { useNavigate } from 'react-router-dom';
import { formatDate } from 'utils/date';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import styles from './clients.module.scss';
import { userService } from 'components/api/users';
import { User } from 'types/user';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';

const BlockedClients: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    sortField: 'createdAt',
    sortValue: 'DESC' as 'ASC' | 'DESC',
    filters: '',
    siteId: 1 as number | undefined,
  });

  const fetchBlockedData = useCallback(async () => {
    try {
      setIsLoading(true);
      // Need to convert filters to a string or query format if needed
      const response = await userService.getBlockedUsers({
        page: params.page,
        limit: params.limit,
        sortField: params.sortField,
        sortValue: params.sortValue,
        siteId: params.siteId,
        filters: params.filters,
      });
      setData(response.data.docs);
      setTotal(response.data.totalDocs);
    } catch (error) {
      console.error('Failed to fetch blocked users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchBlockedData();
  }, [fetchBlockedData]);

  const renderVerificationCell = (params: any) => {
    const isVerified = params.value;
    return (
      <span className={isVerified ? styles.statusVerified : styles.statusUnverified}>
        {isVerified ? 'Verified' : 'Unverified'}
      </span>
    );
  };

  const handleUnblock = async (id: string | number) => {
    try {
      await userService.unblockUser(id);
      dispatch(showToast({ text: 'User unblocked successfully', type: 'success' }));
      fetchBlockedData();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to unblock user';
      dispatch(showToast({ text: message, type: 'error' }));
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 70, sortable: true },
      { field: 'email', headerName: 'Email', width: 220, sortable: true },
      {
        field: 'username',
        headerName: 'Username',
        width: 150,
        sortable: true,
        renderCell: (params) => params.value || '-',
      },
      {
        field: 'isEmailVerified',
        headerName: 'Email Status',
        width: 150,
        renderCell: renderVerificationCell,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 180,
        sortable: true,
        renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleUnblock(params.row.id);
            }}
          >
            Unblock
          </Button>
        ),
      },
    ],
    [navigate],
  );

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setParams((prev) => ({
        ...prev,
        sortField: model[0].field,
        sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
      }));
    }
  };

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.clientsContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Blocked Clients
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <SiteSelector
              value={params.siteId || 'all'}
              onChange={(value) =>
                setParams((prev) => ({
                  ...prev,
                  siteId: value === 'all' ? undefined : (value as number),
                  page: 1,
                }))
              }
              minWidth={130}
            />
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data || []}
          rowCount={total || 0}
          getRowId={(row: any) => row.id}
          columns={columns}
          onSortModelChange={handleSortModelChange}
          loading={isLoading}
          sx={commonGridStyles}
          paginationMode="server"
          sortingMode="server"
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default BlockedClients;
